import {useCallback, useEffect, useState, type FocusEvent, type FormEvent} from 'react'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {Hint} from '@/components/ui/hint'
import {FieldError} from '@/components/field-error'
import {GoogleSignInButton} from '@/components/google-signin-button'
import {RegistroShell} from '@/components/registro-shell'
import {StepProgress} from '@/components/step-progress'
import {CiiuPicker, RiskOptionsPicker} from '@/components/ciiu-risk-picker'
import {useAuth} from '@/hooks/platform/use-auth'
import {useProbeCedula, useRegistroEmpresa, type RegistroEmpresaPayload} from '@/hooks/empresa/identidad/use-registro'
import {useCiiuSearch, useRiskOptions, type CiiuCode} from '@/hooks/common/referencias/use-ciiu'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {track} from '@/lib/telemetry'
import {TipoDocumentoSelect} from '@/components/tipo-documento-select'
import {DOCUMENTO_MAX, documentoNormalizado, type TipoDocumento} from '@/lib/documento'
import {
  validateCedula,
  validateEmail,
  validateNit,
  validatePassword,
  validatePhone,
  validateWorkerCount,
} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

type Modalidad = 'propio' | 'consultor' | 'inci'

// Al cliente se le muestra «Kairos», pero la clave interna sigue siendo `inci` (Inci presta el servicio).
const MODALIDADES: Modalidad[] = ['inci', 'propio', 'consultor']
const RECOMMENDED_MODALIDAD: Modalidad = 'inci'

const TOTAL_STEPS = 3
const STEP_KEYS = ['empresa', 'modalidad', 'rep'] as const

// `existing` pide iniciar sesión: una cuenta con contraseña no se re-adueña (409). Con `authenticated`
// la empresa se crea bajo esa identidad y el backend exige que la cédula coincida.
type RepIdentity = 'unknown' | 'new' | 'existing' | 'authenticated'

type Step1Errors = {nit?: string; name?: string; workerCount?: string}
type Step2Errors = {
  repName?: string
  repLastName?: string
  repEmail?: string
  repPhone?: string
  password?: string
}

function validateRequiredText(raw: string): string | null {
  return raw.trim() ? null : 'errors.nameRequired'
}

function validateOptionalPhone(raw: string): string | null {
  return raw.trim() ? validatePhone(raw) : null
}

export function RegistroEmpresaPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {user, signIn, completeAuth} = useAuth()
  const register = useRegistroEmpresa()
  const probe = useProbeCedula()

  useEffect(() => {
    track('registro_empresa_visto')
  }, [])

  const [step, setStep] = useState(1)
  // El código de referido solo se envía con la modalidad `consultor`: el partner es su asesor.
  const [partnerCode, setPartnerCode] = useState(() => searchParams.get('codigo') ?? '')

  const [nit, setNit] = useState('')
  const [name, setName] = useState('')
  const [ciiu, setCiiu] = useState<CiiuCode | null>(null)
  const [riskId, setRiskId] = useState(() => searchParams.get('riesgo') ?? '')
  const [workerCount, setWorkerCount] = useState(() => searchParams.get('empleados') ?? '')

  // La landing solo trae el código CIIU: se resuelve con la búsqueda del picker y, si no aparece, queda vacío.
  const ciiuPrecargado = searchParams.get('ciiu') ?? ''
  const {data: ciiuBusqueda} = useCiiuSearch(ciiu ? '' : ciiuPrecargado)
  useEffect(() => {
    if (ciiu || !ciiuPrecargado) return
    const exacto = ciiuBusqueda?.items.find((c) => c.code === ciiuPrecargado)
    if (exacto) setCiiu(exacto)
  }, [ciiu, ciiuPrecargado, ciiuBusqueda])

  const [repCedula, setRepCedula] = useState('')
  const [repTipoDocumento, setRepTipoDocumento] = useState<TipoDocumento>('CC')
  const [repIdentity, setRepIdentity] = useState<RepIdentity>(user ? 'authenticated' : 'unknown')
  // Ruta pública: `user` puede llegar después del primer render, cuando el inicializador ya corrió.
  useEffect(() => {
    if (user) setRepIdentity('authenticated')
  }, [user])
  const [repName, setRepName] = useState('')
  const [repLastName, setRepLastName] = useState('')
  const [repEmail, setRepEmail] = useState('')
  const [repPhone, setRepPhone] = useState('')
  const [password, setPassword] = useState('')
  const [existingPassword, setExistingPassword] = useState('')
  const [existingLoginError, setExistingLoginError] = useState<string | null>(null)
  const [existingLoginPending, setExistingLoginPending] = useState(false)

  const [modalidad, setModalidad] = useState<Modalidad>(RECOMMENDED_MODALIDAD)
  const [error, setError] = useState<string | null>(null)
  const [redirecting, setRedirecting] = useState(false)

  const [step1Errors, setStep1Errors] = useState<Step1Errors>({})
  const [step2Errors, setStep2Errors] = useState<Step2Errors>({})
  const [repCedulaError, setRepCedulaError] = useState<string | undefined>()

  // Solo para telemetría: la respuesta de creación no devuelve la clase de riesgo.
  const {data: riskOptions} = useRiskOptions(ciiu?.code ?? null)
  const selectedRiskClass = riskOptions?.find((option) => option.id === riskId)?.risk_class

  function onCiiuChange(next: CiiuCode | null) {
    setCiiu(next)
    setRiskId('')
  }

  function onModalidadChange(next: Modalidad) {
    setModalidad(next)
    if (next !== 'consultor') setPartnerCode('')
  }

  function blurStep1<K extends keyof Step1Errors>(field: K, validate: (raw: string) => string | null) {
    return (e: FocusEvent<HTMLInputElement>) =>
      setStep1Errors((prev) => ({...prev, [field]: validate(e.target.value) ?? undefined}))
  }

  function blurStep2<K extends keyof Step2Errors>(field: K, validate: (raw: string) => string | null) {
    return (e: FocusEvent<HTMLInputElement>) =>
      setStep2Errors((prev) => ({...prev, [field]: validate(e.target.value) ?? undefined}))
  }

  function onRepCedulaChange(next: string) {
    setRepCedula(documentoNormalizado(repTipoDocumento, next))
    if (repIdentity !== 'authenticated') setRepIdentity('unknown')
    setRepCedulaError(undefined)
  }

  async function probeCedula() {
    setError(null)
    const cedula = repCedula.trim()
    if (cedula.length < 6) return
    const cedulaError = validateCedula(cedula, repTipoDocumento)
    if (cedulaError) {
      setRepCedulaError(cedulaError)
      return
    }
    setRepCedulaError(undefined)
    try {
      const result = await probe.mutateAsync(cedula)
      setRepIdentity(result.status === 'existing_with_password' ? 'existing' : 'new')
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  // Al adoptar el token cambia `user`, y el efecto de arriba pasa la identidad a 'authenticated'.
  async function handleExistingLogin() {
    setExistingLoginError(null)
    setExistingLoginPending(true)
    const {error: loginError} = await signIn(repCedula.trim(), existingPassword)
    setExistingLoginPending(false)
    if (loginError) setExistingLoginError(loginError)
  }

  const handleExistingGoogle = useCallback(
    async (credential: string) => {
      setExistingLoginError(null)
      try {
        const {data} = await api.post('/auth/google', {credential})
        await completeAuth(data.access_token)
      } catch (err) {
        setExistingLoginError(translateApiError(err))
      }
    },
    [completeAuth]
  )

  const step1Valid =
    Boolean(nit && name && ciiu && riskId && workerCount) &&
    validateNit(nit) === null &&
    validateRequiredText(name) === null &&
    validateWorkerCount(workerCount) === null
  const stepRepValid =
    repIdentity === 'authenticated' ||
    (repIdentity === 'new' &&
      Boolean(repName && repLastName && repEmail && password) &&
      validateRequiredText(repName) === null &&
      validateRequiredText(repLastName) === null &&
      validateEmail(repEmail) === null &&
      validateOptionalPhone(repPhone) === null &&
      validatePassword(password) === null)

  function goNext() {
    setError(null)
    if (step === 1 && !step1Valid) return
    if (step === 1) track('registro_empresa_paso1_ok')
    if (step === 2) track('registro_empresa_paso2_ok')
    setStep((s) => Math.min(s + 1, TOTAL_STEPS))
  }

  function goBack() {
    setError(null)
    setStep((s) => Math.max(s - 1, 1))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!ciiu) return
    // Con sesión el backend ignora nombre y correo, pero el payload los exige.
    const [authFirst, ...authRest] = (user?.full_name ?? '').split(' ')
    const payload: RegistroEmpresaPayload = {
      nit: nit.trim(),
      name: name.trim(),
      ciiu_code: ciiu.code,
      risk_classification_id: riskId,
      worker_count: Number(workerCount),
      // Manda la cédula tecleada: el backend exige que coincida con la sesión, así una cuenta
      // equivocada falla en vez de cambiar la identidad en silencio.
      legal_rep_cedula: repCedula.trim() || (user ? user.cedula : ''),
      legal_rep_tipo_documento: user ? user.tipo_documento : repTipoDocumento,
      legal_rep_name: user ? authFirst || user.full_name : repName.trim(),
      legal_rep_last_name: user ? authRest.join(' ') || authFirst || user.full_name : repLastName.trim(),
      legal_rep_email: user ? (user.email ?? repEmail.trim()) : repEmail.trim(),
      legal_rep_phone: repPhone.trim() || null,
      password: user ? null : password,
      sst_modalidad: modalidad,
      partner_code: modalidad === 'consultor' ? partnerCode.trim() || null : null,
    }
    try {
      const result = await register.mutateAsync(payload)
      track('empresa_creada', {
        tier: 'free',
        risk_class: selectedRiskClass ?? 0,
        worker_count: Number(workerCount),
        modalidad,
        con_partner: modalidad === 'consultor' && !!partnerCode.trim(),
      })
      setRedirecting(true)
      if (result.access_token) await completeAuth(result.access_token)
      navigate(`/businesses/${result.business_id}/sgsst`, {replace: true})
    } catch (err) {
      setRedirecting(false)
      setError(translateApiError(err))
    }
  }

  const canSubmit = step1Valid && stepRepValid

  return (
    <RegistroShell width="md" eyebrow={t('registroEmpresa.eyebrow')}>
      <h1 className="font-heading text-2xl tracking-tight text-foreground">{t('registroEmpresa.title')}</h1>

      <div className="mt-6">
        <StepProgress
          step={step}
          total={TOTAL_STEPS}
          testId="registro-empresa-progress"
          label={`${t('registroEmpresa.stepIndicator', {step, total: TOTAL_STEPS})} · ${t(`registroEmpresa.steps.${STEP_KEYS[step - 1]}`)}`}
        />
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-5" data-testid="registro-empresa-form">
        {step === 1 && (
          <fieldset className="min-w-0 space-y-4" data-testid="registro-step-empresa">
            <legend className="sr-only">{t('registroEmpresa.steps.empresa')}</legend>
            <div className="space-y-2">
              <Label htmlFor="empresa-nit">{t('business.nit')}</Label>
              <Input
                maxLength={tope('NIT')}
                id="empresa-nit"
                value={nit}
                onChange={(e) => {
                  setNit(e.target.value)
                  setStep1Errors((prev) => ({...prev, nit: undefined}))
                }}
                onBlur={blurStep1('nit', validateNit)}
                aria-invalid={!!step1Errors.nit}
                placeholder="900123456-7"
                required
              />
              <FieldError error={step1Errors.nit} />
              <Hint>{t('business.nitHint')}</Hint>
            </div>
            <div className="space-y-2">
              <Label htmlFor="empresa-name">{t('business.name')}</Label>
              <Input
                id="empresa-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  setStep1Errors((prev) => ({...prev, name: undefined}))
                }}
                onBlur={blurStep1('name', validateRequiredText)}
                aria-invalid={!!step1Errors.name}
                maxLength={tope('RAZ')}
                required
              />
              <FieldError error={step1Errors.name} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ciiu_trigger">{t('registroEmpresa.ciiuLabel')}</Label>
              <CiiuPicker ciiu={ciiu} onChange={onCiiuChange} />
            </div>
            {ciiu && (
              <div className="space-y-2">
                <Label>{t('registroEmpresa.riskLabel')}</Label>
                <RiskOptionsPicker ciiuCode={ciiu.code} riskId={riskId} onPick={setRiskId} />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="empresa-workers">{t('registroEmpresa.workerCount')}</Label>
              <Input
                maxLength={tope('NUM')}
                id="empresa-workers"
                inputMode="numeric"
                value={workerCount}
                onChange={(e) => {
                  setWorkerCount(e.target.value.replace(/\D/g, ''))
                  setStep1Errors((prev) => ({...prev, workerCount: undefined}))
                }}
                onBlur={blurStep1('workerCount', validateWorkerCount)}
                aria-invalid={!!step1Errors.workerCount}
                required
              />
              <FieldError error={step1Errors.workerCount} />
            </div>
          </fieldset>
        )}

        {step === 2 && (
          <fieldset className="space-y-3" data-testid="registro-step-modalidad">
            <legend className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('registroEmpresa.modalidadSection')}
            </legend>
            <div className="space-y-2" data-testid="modalidad-options">
              {MODALIDADES.map((m) => (
                <label
                  key={m}
                  data-testid={`modalidad-option-${m}`}
                  className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-input bg-card px-3.5 py-3 transition-colors hover:border-primary/50 has-checked:border-primary has-checked:bg-accent/45">
                  <input
                    type="radio"
                    name="modalidad"
                    value={m}
                    checked={modalidad === m}
                    onChange={() => onModalidadChange(m)}
                    className="mt-0.5 accent-primary"
                  />
                  <span className="min-w-0 text-sm">
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{t(`registroEmpresa.modalidad.${m}.title`)}</span>
                      {m === RECOMMENDED_MODALIDAD && (
                        <Tag tone="neutral" data-testid="modalidad-recomendado">
                          {t('registroEmpresa.recomendado')}
                        </Tag>
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {t(`registroEmpresa.modalidad.${m}.body`)}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {modalidad === 'consultor' && (
              <div className="space-y-2" data-testid="registro-partner-field">
                <Label htmlFor="empresa-partner">{t('registroEmpresa.partnerCode')}</Label>
                <Input
                  maxLength={tope('CODE')}
                  id="empresa-partner"
                  data-testid="registro-partner-code"
                  value={partnerCode}
                  onChange={(e) => setPartnerCode(e.target.value)}
                  placeholder={t('registroEmpresa.partnerCodePlaceholder')}
                />
              </div>
            )}
          </fieldset>
        )}

        {step === 3 && (
          <fieldset className="min-w-0 space-y-4" data-testid="registro-step-rep">
            <legend className="sr-only">{t('registroEmpresa.steps.rep')}</legend>
            {repIdentity === 'authenticated' && user ? (
              <div className="space-y-2" data-testid="registro-rep-authenticated">
                <p className="rounded-lg bg-secondary px-3 py-2.5 text-sm text-foreground">
                  {t('registroEmpresa.cedulaFirst.loggedInAs', {name: user.full_name})}
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="rep-cedula">{t('documento.label')}</Label>
                  <div className="flex gap-2">
                    <TipoDocumentoSelect
                      value={repTipoDocumento}
                      onChange={setRepTipoDocumento}
                      testId="rep-tipo-documento"
                    />
                    <Input
                      id="rep-cedula"
                      inputMode={repTipoDocumento === 'PA' ? 'text' : 'numeric'}
                      maxLength={DOCUMENTO_MAX[repTipoDocumento]}
                      value={repCedula}
                      onChange={(e) => onRepCedulaChange(e.target.value)}
                      onBlur={() => repIdentity === 'unknown' && probeCedula()}
                      aria-invalid={!!repCedulaError}
                      required
                    />
                    {repIdentity === 'unknown' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={probeCedula}
                        disabled={repCedula.trim().length < 6 || probe.isPending}
                        data-testid="registro-rep-cedula-continue">
                        {probe.isPending
                          ? t('registroEmpresa.cedulaFirst.checking')
                          : t('registroEmpresa.cedulaFirst.continue')}
                      </Button>
                    )}
                  </div>
                  <FieldError error={repCedulaError} />
                  <Hint>{t('auth.cedulaHint')}</Hint>
                </div>

                {repIdentity === 'existing' && (
                  <div className="notice-amber space-y-3 rounded-lg px-3.5 py-3" data-testid="registro-rep-existing">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t('registroEmpresa.cedulaFirst.existingTitle')}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rep-existing-password">{t('auth.password')}</Label>
                      <Input
                        maxLength={tope('PASS')}
                        id="rep-existing-password"
                        type="password"
                        value={existingPassword}
                        onChange={(e) => {
                          setExistingPassword(e.target.value)
                          setExistingLoginError(null)
                        }}
                        aria-invalid={!!existingLoginError}
                      />
                      {existingLoginError && (
                        <p className="text-xs font-medium text-destructive">{existingLoginError}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleExistingLogin}
                      disabled={!existingPassword || existingLoginPending}
                      data-testid="registro-rep-login-password">
                      {existingLoginPending ? t('registroEmpresa.cedulaFirst.loggingIn') : t('auth.signIn')}
                    </Button>
                    <GoogleSignInButton onCredential={handleExistingGoogle} />
                  </div>
                )}

                {repIdentity === 'new' && (
                  <div className="space-y-4" data-testid="registro-rep-new-fields">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="rep-name">{t('perfil.name')}</Label>
                        <Input
                          id="rep-name"
                          value={repName}
                          onChange={(e) => {
                            setRepName(e.target.value)
                            setStep2Errors((prev) => ({...prev, repName: undefined}))
                          }}
                          onBlur={blurStep2('repName', validateRequiredText)}
                          aria-invalid={!!step2Errors.repName}
                          maxLength={tope('NOM')}
                          required
                        />
                        <FieldError error={step2Errors.repName} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rep-last-name">{t('perfil.lastName')}</Label>
                        <Input
                          id="rep-last-name"
                          value={repLastName}
                          onChange={(e) => {
                            setRepLastName(e.target.value)
                            setStep2Errors((prev) => ({...prev, repLastName: undefined}))
                          }}
                          onBlur={blurStep2('repLastName', validateRequiredText)}
                          aria-invalid={!!step2Errors.repLastName}
                          maxLength={tope('NOM')}
                          required
                        />
                        <FieldError error={step2Errors.repLastName} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rep-email">{t('perfil.email')}</Label>
                      <Input
                        id="rep-email"
                        type="email"
                        inputMode="email"
                        value={repEmail}
                        onChange={(e) => {
                          setRepEmail(e.target.value)
                          setStep2Errors((prev) => ({...prev, repEmail: undefined}))
                        }}
                        onBlur={blurStep2('repEmail', validateEmail)}
                        aria-invalid={!!step2Errors.repEmail}
                        maxLength={tope('MAIL')}
                        required
                      />
                      <FieldError error={step2Errors.repEmail} />
                      <Hint>{t('perfil.emailHint')}</Hint>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rep-phone">{t('registroEmpresa.phoneOptional')}</Label>
                      <Input
                        id="rep-phone"
                        inputMode="tel"
                        value={repPhone}
                        maxLength={tope('TEL')}
                        aria-invalid={!!step2Errors.repPhone}
                        onChange={(e) => {
                          setRepPhone(e.target.value)
                          setStep2Errors((prev) => ({...prev, repPhone: undefined}))
                        }}
                        onBlur={blurStep2('repPhone', validateOptionalPhone)}
                      />
                      <FieldError error={step2Errors.repPhone} />
                      <Hint>{t('perfil.phoneHint')}</Hint>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rep-password">{t('auth.password')}</Label>
                      <Input
                        maxLength={tope('PASS')}
                        id="rep-password"
                        type="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value)
                          setStep2Errors((prev) => ({...prev, password: undefined}))
                        }}
                        onBlur={blurStep2('password', validatePassword)}
                        aria-invalid={!!step2Errors.password}
                        minLength={8}
                        required
                      />
                      <FieldError error={step2Errors.password} />
                      <Hint>{t('common.passwordHint')}</Hint>
                    </div>
                  </div>
                )}
              </>
            )}
          </fieldset>
        )}

        {error && (
          <p className="notice-red rounded-lg px-3.5 py-3 text-sm font-medium" data-testid="registro-empresa-error">
            {error}
          </p>
        )}

        <div className="surface-divider flex items-center justify-between gap-3 border-t border-dashed pt-5">
          <Button type="button" variant="outline" onClick={goBack} disabled={step <= 1} data-testid="registro-back">
            {t('common.back')}
          </Button>
          {step < TOTAL_STEPS ? (
            <Button
              key="next"
              type="button"
              onClick={goNext}
              disabled={step === 1 && !step1Valid}
              data-testid="registro-next">
              {t('registroEmpresa.next')}
            </Button>
          ) : (
            <Button
              key="submit"
              type="submit"
              disabled={!canSubmit || register.isPending || redirecting}
              data-testid="registro-submit">
              {t('registroEmpresa.submit')}
            </Button>
          )}
        </div>

        <Hint className="text-center">{t('registroEmpresa.freeNote')}</Hint>
        <Link to="/login" className="block text-center text-sm text-primary hover:underline">
          {t('registroEmpresa.haveAccount')}
        </Link>
      </form>
    </RegistroShell>
  )
}
